import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  doc, getDoc, setDoc, collection, query, where, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import usePlanFeatures from '../hooks/usePlanFeatures';
import { Link } from 'react-router-dom';
import {
  Globe, Clock, Scissors, Save, CheckCircle2, Loader2, ExternalLink, Lock, Sparkles,
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const IS_SAAS = process.env.REACT_APP_IS_SAAS === 'true';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DEFAULT_HOURS = {
  monday:    { isOpen: true,  open: '09:00', close: '19:00' },
  tuesday:   { isOpen: true,  open: '09:00', close: '19:00' },
  wednesday: { isOpen: true,  open: '09:00', close: '19:00' },
  thursday:  { isOpen: true,  open: '09:00', close: '19:00' },
  friday:    { isOpen: true,  open: '09:00', close: '19:00' },
  saturday:  { isOpen: true,  open: '09:00', close: '17:00' },
  sunday:    { isOpen: false, open: null,    close: null     },
};

export default function BusinessProfileSetup() {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { isFeatureAllowed } = usePlanFeatures();
  const isCustomDomainAllowed = isFeatureAllowed('customDomain');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);

  const [form, setForm] = useState({
    slug: '',
    publicName: '',
    description: '',
    workingHours: DEFAULT_HOURS,
    activeServiceIds: [],
    status: 'active',
  });

  const docId = user?.businessId;

  useEffect(() => {
    if (!docId) return;
    const loadData = async () => {
      try {
        const [profileSnap, servicesSnap] = await Promise.all([
          getDoc(doc(db, 'business_profiles', docId)),
          getDocs(
            query(
              collection(db, 'services'),
              where('businessId', '==', docId),
              where('status', '!=', 'deleted')
            )
          ),
        ]);

        const svcList = servicesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setServices(svcList);

        if (profileSnap.exists()) {
          const data = profileSnap.data();
          setForm({
            slug: data.slug || '',
            publicName: data.publicName || '',
            description: data.description || '',
            workingHours: data.workingHours || DEFAULT_HOURS,
            activeServiceIds: (data.activeServices || []).map((s) => s.id),
            status: data.status || 'active',
          });
        }
      } catch (err) {
        toast.error(t('businessProfile.loadError') + ': ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [docId]);

  const handleHoursChange = (day, field, value) => {
    setForm((prev) => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: { ...prev.workingHours[day], [field]: value },
      },
    }));
  };

  const toggleService = (id) => {
    setForm((prev) => ({
      ...prev,
      activeServiceIds: prev.activeServiceIds.includes(id)
        ? prev.activeServiceIds.filter((x) => x !== id)
        : [...prev.activeServiceIds, id],
    }));
  };

  const handleSave = async () => {
    if (!form.slug.trim()) { toast.error(t('businessProfile.slugRequired')); return; }
    if (!form.publicName.trim()) { toast.error(t('businessProfile.publicNameRequired')); return; }
    if (!/^[a-z0-9-]+$/.test(form.slug)) {
      toast.error(t('businessProfile.slugInvalid'));
      return;
    }

    setSaving(true);
    try {
      const slugToSave = form.slug.trim().toLowerCase();
      const slugSnap = await getDocs(
        query(collection(db, 'business_profiles'), where('slug', '==', slugToSave))
      );
      const conflict = slugSnap.docs.find((d) => d.id !== docId);
      if (conflict) {
        toast.error(t('businessProfile.slugConflict'));
        setSaving(false);
        return;
      }

      const activeServices = services
        .filter((s) => form.activeServiceIds.includes(s.id))
        .map((s) => ({
          id: s.id,
          name: s.name,
          price: s.price || 0,
          durationMinutes: s.durationMinutes || 30,
        }));

      await setDoc(
        doc(db, 'business_profiles', docId),
        {
          businessId: docId,
          slug: slugToSave,
          publicName: form.publicName.trim(),
          description: form.description.trim(),
          workingHours: form.workingHours,
          activeServices,
          status: form.status,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      toast.success(t('businessProfile.saveSuccess'));
    } catch (err) {
      toast.error(t('businessProfile.saveError') + ': ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!IS_SAAS) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center text-gray-500">
        {t('businessProfile.saasOnly')}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
      </div>
    );
  }

  const bookingUrl = `${window.location.origin}/book/${form.slug}`;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover draggable />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('businessProfile.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('businessProfile.subtitle')}</p>
        </div>
        {form.slug && (
          <a
            href={bookingUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center text-sm text-blue-600 hover:underline"
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            {t('businessProfile.previewLink')}
          </a>
        )}
      </div>

      {/* URL & Genel Bilgiler */}
      <div className="bg-white rounded-xl border p-5 space-y-4">
        <div className="flex items-center space-x-2 mb-2">
          <Globe className="h-5 w-5 text-blue-500" />
          <h2 className="font-semibold text-gray-800">{t('businessProfile.generalTitle')}</h2>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('businessProfile.slugLabel')} <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
            <span className="px-3 py-2 bg-gray-50 text-gray-500 text-sm border-r">/book/</span>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              placeholder={t('businessProfile.slugPlaceholder')}
              className="flex-1 px-3 py-2 text-sm outline-none"
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">{t('businessProfile.slugHint')}</p>
          {form.slug && (
            <p className="text-xs text-blue-600 mt-1 font-medium">
              Randevu URL: <span className="font-mono">{window.location.origin}/book/{form.slug}</span>
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('businessProfile.publicNameLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.publicName}
            onChange={(e) => setForm((p) => ({ ...p, publicName: e.target.value }))}
            placeholder={t('businessProfile.publicNamePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('businessProfile.descriptionLabel')}</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder={t('businessProfile.descriptionPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-sm font-medium text-gray-700">{t('businessProfile.bookingToggle')}</span>
          <button
            onClick={() => setForm((p) => ({ ...p, status: p.status === 'active' ? 'inactive' : 'active' }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.status === 'active' ? 'bg-blue-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                form.status === 'active' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Çalışma Saatleri */}
      <div className="bg-white rounded-xl border p-5">
        <div className="flex items-center space-x-2 mb-4">
          <Clock className="h-5 w-5 text-blue-500" />
          <h2 className="font-semibold text-gray-800">{t('businessProfile.hoursTitle')}</h2>
        </div>
        <div className="space-y-3">
          {DAY_KEYS.map((day) => {
            const cfg = form.workingHours[day] || { isOpen: false, open: '09:00', close: '19:00' };
            return (
              <div key={day} className="flex items-center space-x-3">
                <button
                  onClick={() => handleHoursChange(day, 'isOpen', !cfg.isOpen)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                    cfg.isOpen ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                      cfg.isOpen ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className={`text-sm w-24 ${cfg.isOpen ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                  {t(`businessProfile.${day}`)}
                </span>
                {cfg.isOpen ? (
                  <div className="flex items-center space-x-2 text-sm">
                    <input
                      type="time"
                      value={cfg.open || '09:00'}
                      onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                      className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="time"
                      value={cfg.close || '19:00'}
                      onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                      className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">{t('businessProfile.dayClosed')}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Hizmetler */}
      <div className="bg-white rounded-xl border p-5">
        <div className="flex items-center space-x-2 mb-4">
          <Scissors className="h-5 w-5 text-blue-500" />
          <h2 className="font-semibold text-gray-800">{t('businessProfile.servicesTitle')}</h2>
        </div>
        {services.length === 0 ? (
          <p className="text-sm text-gray-500">{t('businessProfile.servicesEmpty')}</p>
        ) : (
          <div className="space-y-2">
            {services.map((svc) => {
              const active = form.activeServiceIds.includes(svc.id);
              return (
                <button
                  key={svc.id}
                  onClick={() => toggleService(svc.id)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all ${
                    active
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                      active ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                    }`}>
                      {active && <CheckCircle2 className="h-3 w-3 text-white" />}
                    </div>
                    <span className="text-sm font-medium text-gray-800">{svc.name}</span>
                  </div>
                  <span className="text-sm text-gray-500">₺{svc.price} • {svc.durationMinutes || 30}{t('businessProfile.durationUnit')}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Özel Alan Adı */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="flex items-center gap-2 p-5 border-b border-gray-100">
          <Globe className="h-5 w-5 text-purple-500" />
          <h2 className="font-semibold text-gray-800">Özel Alan Adı</h2>
          <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700">
            Premium
          </span>
        </div>

        {isCustomDomainAllowed ? (
          /* Premium kullanıcı — özellik yakında */
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Sparkles className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Bu özellik yakında aktif olacak</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Premium planınıza dahil. Özel alan adı kurulumu yapıldığında randevu sayfanız
                  ve uygulama girişiniz kendi domain'inizden çalışacak.
                </p>
              </div>
            </div>
            <div className="space-y-3 opacity-50 pointer-events-none select-none">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Randevu sayfası alan adı</label>
                <input
                  type="text"
                  disabled
                  placeholder="book.isletmeniz.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Uygulama girişi alan adı</label>
                <input
                  type="text"
                  disabled
                  placeholder="app.isletmeniz.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                />
              </div>
            </div>
          </div>
        ) : (
          /* Pro kullanıcı — upgrade banner */
          <div className="p-5">
            <div className="flex items-start gap-4">
              <Lock className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-700">Özel Alan Adı — Premium Plan Gerekli</p>
                <p className="text-xs text-gray-500 mt-1">
                  Randevu sayfanız ve uygulama girişiniz için kendi domain adresinizi kullanın.
                  Şu an adresiniz: <span className="font-mono text-blue-600">{window.location.origin}/book/{form.slug || 'slug'}</span>
                </p>
                {IS_SAAS && (
                  <Link
                    to="/billing"
                    className="inline-flex items-center mt-3 px-3 py-1.5 text-xs font-semibold rounded-lg text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    Premium'a Geç
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Kaydet */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full flex items-center justify-center py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-60"
      >
        {saving ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('businessProfile.savingBtn')}</>
        ) : (
          <><Save className="h-4 w-4 mr-2" /> {t('businessProfile.saveBtn')}</>
        )}
      </button>
    </div>
  );
}
